import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useData } from '../../contexts/DataContext';
import { toast } from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import RichTextEditor from '../common/RichTextEditor';

const { FiSave, FiX, FiUpload, FiImage, FiTrash2, FiPlus, FiHome, FiSettings, FiFileText, FiMail, FiMapPin, FiInfo, FiPhone } = FiIcons;

const PropertyForm = ({ property, onClose }) => {
  const { createProperty, updateProperty } = useData();
  const fileInputRef = useRef(null);
  const backgroundImageInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('basic');
  
  // Helper function to convert string instructions to object format with default titles
  const normalizeInstruction = (instruction, defaultTitle) => {
    if (!instruction) return null;
    if (typeof instruction === 'string') {
      return { title: defaultTitle || '', content: instruction };
    }
    if (typeof instruction === 'object' && instruction.content !== undefined) {
      // Ensure title exists, use default if missing
      return { 
        title: instruction.title || defaultTitle || '', 
        content: instruction.content 
      };
    }
    return null;
  };

  const [formData, setFormData] = useState({
    name: property?.name || '',
    address: property?.address || '',
    image: property?.image || '',
    backgroundImage: property?.backgroundImage || '',
    wifiName: property?.wifiName || '',
    wifiPassword: property?.wifiPassword || '',
    checkInTime: property?.checkInTime || '15:00',
    checkOutTime: property?.checkOutTime || '11:00',
    houseRules: property?.houseRules || '',
    checkoutProcedures: property?.checkoutProcedures || '',
    tvInstructions: normalizeInstruction(property?.tvInstructions, 'TV Instructions'),
    fireplaceInstructions: normalizeInstruction(property?.fireplaceInstructions, 'Fireplace Instructions'),
    supportContact: property?.supportContact || '',
    localRecommendations: property?.localRecommendations || '',
    additionalInstructions: property?.additionalInstructions || []
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRichTextChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleInstructionTitleChange = (field, title) => {
    const current = formData[field];
    if (current && typeof current === 'object') {
      setFormData({ ...formData, [field]: { ...current, title } });
    }
  };

  const handleInstructionContentChange = (field, content) => {
    const current = formData[field];
    if (current && typeof current === 'object') {
      setFormData({ ...formData, [field]: { ...current, content } });
    }
  };

  const handleAddInstruction = () => {
    setFormData({
      ...formData,
      additionalInstructions: [
        ...(formData.additionalInstructions || []),
        { title: '', content: '' }
      ]
    });
  };

  const handleRemoveInstruction = (index) => {
    const newInstructions = formData.additionalInstructions.filter((_, i) => i !== index);
    setFormData({ ...formData, additionalInstructions: newInstructions });
  };

  const handleInstructionChange = (index, field, value) => {
    const newInstructions = [...(formData.additionalInstructions || [])];
    newInstructions[index] = { ...newInstructions[index], [field]: value };
    setFormData({ ...formData, additionalInstructions: newInstructions });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) { // 5MB limit
        toast.error("Image file is too large. Please choose an image under 5MB.");
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          // Create canvas to compress image
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 1200;
          const MAX_HEIGHT = 800;
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions
          if (width > height) {
            if (width > MAX_WIDTH) {
              height = (height * MAX_WIDTH) / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = (width * MAX_HEIGHT) / height;
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression (0.85 quality)
          const compressed = canvas.toDataURL('image/jpeg', 0.85);
          setFormData({ ...formData, image: compressed });
          toast.success('Image compressed and ready');
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBackgroundImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10000000) { // 10MB limit for background images
        toast.error("Image file is too large. Please choose an image under 10MB.");
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          // Create canvas to compress image (larger dimensions for background)
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 1920;
          const MAX_HEIGHT = 1080;
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions
          if (width > height) {
            if (width > MAX_WIDTH) {
              height = (height * MAX_WIDTH) / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = (width * MAX_HEIGHT) / height;
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression (0.8 quality for larger images)
          const compressed = canvas.toDataURL('image/jpeg', 0.8);
          setFormData({ ...formData, backgroundImage: compressed });
          toast.success('Background image compressed and ready');
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, image: '' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeBackgroundImage = () => {
    setFormData({ ...formData, backgroundImage: '' });
    if (backgroundImageInputRef.current) {
      backgroundImageInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert instruction objects to string format for backward compatibility
      // If title exists, we'll store as object, otherwise as string
      const submitData = {
        ...formData,
        tvInstructions: formData.tvInstructions 
          ? (formData.tvInstructions.title || formData.tvInstructions.content ? formData.tvInstructions : null)
          : null,
        fireplaceInstructions: formData.fireplaceInstructions
          ? (formData.fireplaceInstructions.title || formData.fireplaceInstructions.content ? formData.fireplaceInstructions : null)
          : null
      };
      
      if (property) {
        await updateProperty(property.id, submitData);
        toast.success('Property updated successfully!');
        // Stay on the form after updating
      } else {
        await createProperty(submitData);
        toast.success('Property created successfully!');
        // Close form after creating new property
        onClose();
      }
    } catch (error) {
      toast.error('Failed to save property. Please try again.');
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: FiHome },
    { id: 'images', label: 'Images', icon: FiImage },
    { id: 'rules', label: 'Rules & Procedures', icon: FiFileText },
    { id: 'instructions', label: 'Instructions', icon: FiSettings },
    { id: 'support', label: 'Support Contact', icon: FiPhone },
    { id: 'local', label: 'Local Recommendations', icon: FiMapPin }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200"
    >
      <div className="flex justify-between items-center p-6 border-b border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900">
          {property ? 'Edit Property' : 'Add New Property'}
        </h2>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <SafeIcon icon={FiX} className="text-xl" />
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 px-6">
        <div className="flex space-x-1 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap
                  ${activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }
                `}
              >
                <SafeIcon icon={Icon} className="text-base" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="p-6">
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Property Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address *
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      WiFi Network Name
                    </label>
                    <input
                      type="text"
                      name="wifiName"
                      value={formData.wifiName}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      WiFi Password
                    </label>
                    <input
                      type="text"
                      name="wifiPassword"
                      value={formData.wifiPassword}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Check-in Time
                    </label>
                    <input
                      type="time"
                      name="checkInTime"
                      value={formData.checkInTime}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Check-out Time
                    </label>
                    <input
                      type="time"
                      name="checkOutTime"
                      value={formData.checkOutTime}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Images Tab */}
          {activeTab === 'images' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Property Images</h3>
                <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Property Image</label>
                    <div className="flex items-start space-x-4">
                      <div className="relative w-32 h-24 bg-gray-100 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
                        {formData.image ? (
                          <img src={formData.image} alt="Property preview" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <SafeIcon icon={FiImage} className="text-3xl" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleImageUpload}
                          accept="image/*"
                          className="hidden"
                          id="property-image-upload"
                        />
                        <div className="flex space-x-2">
                          <label
                            htmlFor="property-image-upload"
                            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <div className="flex items-center space-x-2">
                              <SafeIcon icon={FiUpload} />
                              <span>Upload Image</span>
                            </div>
                          </label>
                          {formData.image && (
                            <button
                              type="button"
                              onClick={removeImage}
                              className="px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm font-medium text-red-600 hover:bg-red-100 transition-colors"
                            >
                              <div className="flex items-center space-x-2">
                                <SafeIcon icon={FiTrash2} />
                                <span>Remove</span>
                              </div>
                            </button>
                          )}
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          Recommended: 800x600px or larger (Max 5MB)
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Guest Login Background Image</label>
                    <p className="text-xs text-gray-500 mb-2">This image will be displayed as a full-screen background on the guest login page</p>
                    <div className="flex items-start space-x-4">
                      <div className="relative w-48 h-32 bg-gray-100 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
                        {formData.backgroundImage ? (
                          <img src={formData.backgroundImage} alt="Background preview" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <SafeIcon icon={FiImage} className="text-3xl" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <input
                          type="file"
                          ref={backgroundImageInputRef}
                          onChange={handleBackgroundImageUpload}
                          accept="image/*"
                          className="hidden"
                          id="background-image-upload"
                        />
                        <div className="flex space-x-2">
                          <label
                            htmlFor="background-image-upload"
                            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <div className="flex items-center space-x-2">
                              <SafeIcon icon={FiUpload} />
                              <span>Upload Background</span>
                            </div>
                          </label>
                          {formData.backgroundImage && (
                            <button
                              type="button"
                              onClick={removeBackgroundImage}
                              className="px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm font-medium text-red-600 hover:bg-red-100 transition-colors"
                            >
                              <div className="flex items-center space-x-2">
                                <SafeIcon icon={FiTrash2} />
                                <span>Remove</span>
                              </div>
                            </button>
                          )}
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          Recommended: 1920x1080px or larger (Max 10MB). This will be the full-screen background on the guest login page.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rules & Procedures Tab */}
          {activeTab === 'rules' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">House Rules & Procedures</h3>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      House Rules
                    </label>
                    <RichTextEditor
                      value={formData.houseRules}
                      onChange={(value) => handleRichTextChange('houseRules', value)}
                      placeholder="No smoking, no parties, quiet hours 10pm-8am..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Checkout Procedures
                    </label>
                    <RichTextEditor
                      value={formData.checkoutProcedures}
                      onChange={(value) => handleRichTextChange('checkoutProcedures', value)}
                      placeholder="Strip beds, start dishwasher, lock all doors..."
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Instructions Tab */}
          {activeTab === 'instructions' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Property Instructions</h3>
                <div className="space-y-6">
                  {formData.tvInstructions && (
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-start justify-between mb-3">
                        <input
                          type="text"
                          value={formData.tvInstructions.title || ''}
                          onChange={(e) => handleInstructionTitleChange('tvInstructions', e.target.value)}
                          placeholder="TV Instructions"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium bg-white"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, tvInstructions: null })}
                          className="ml-3 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center space-x-1 text-sm font-medium"
                          title="Remove TV Instructions"
                        >
                          <SafeIcon icon={FiTrash2} />
                          <span>Remove</span>
                        </button>
                      </div>
                      <div className="w-full">
                        <RichTextEditor
                          value={formData.tvInstructions.content || ''}
                          onChange={(value) => handleInstructionContentChange('tvInstructions', value)}
                          placeholder="Samsung TV, use remote to power on..."
                          className="min-h-[120px]"
                        />
                      </div>
                    </div>
                  )}

                  {formData.fireplaceInstructions && (
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-start justify-between mb-3">
                        <input
                          type="text"
                          value={formData.fireplaceInstructions.title || ''}
                          onChange={(e) => handleInstructionTitleChange('fireplaceInstructions', e.target.value)}
                          placeholder="Fireplace Instructions"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium bg-white"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, fireplaceInstructions: null })}
                          className="ml-3 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center space-x-1 text-sm font-medium"
                          title="Remove Fireplace Instructions"
                        >
                          <SafeIcon icon={FiTrash2} />
                          <span>Remove</span>
                        </button>
                      </div>
                      <div className="w-full">
                        <RichTextEditor
                          value={formData.fireplaceInstructions.content || ''}
                          onChange={(value) => handleInstructionContentChange('fireplaceInstructions', value)}
                          placeholder="Electric fireplace, press power button..."
                          className="min-h-[120px]"
                        />
                      </div>
                    </div>
                  )}

                  {!formData.tvInstructions && !formData.fireplaceInstructions && (
                    <div className="text-center py-4 bg-gray-50 rounded-lg border border-gray-200 border-dashed">
                      <p className="text-sm text-gray-500 mb-4">
                        Add TV or Fireplace instructions using the buttons below, or add custom instructions in the Additional Instructions section.
                      </p>
                    </div>
                  )}

                  <div className="flex gap-2">
                    {!formData.tvInstructions && (
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, tvInstructions: { title: 'TV Instructions', content: '<p></p>' } })}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        <SafeIcon icon={FiPlus} />
                        <span>Add TV Instructions</span>
                      </button>
                    )}
                    {!formData.fireplaceInstructions && (
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, fireplaceInstructions: { title: 'Fireplace Instructions', content: '<p></p>' } })}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        <SafeIcon icon={FiPlus} />
                        <span>Add Fireplace Instructions</span>
                      </button>
                    )}
                  </div>

                  {/* Additional Instructions */}
                  <div className="border-t border-gray-200 pt-6">
                    <div className="mb-4">
                      <h4 className="text-md font-semibold text-gray-900 mb-1">Additional Instructions</h4>
                      <p className="text-xs text-gray-500">
                        Add custom instruction sections for your property
                      </p>
                    </div>

                    {formData.additionalInstructions && formData.additionalInstructions.length > 0 ? (
                      <div className="space-y-4">
                        {formData.additionalInstructions.map((instruction, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <div className="flex items-start justify-between mb-3">
                              <input
                                type="text"
                                value={instruction.title || ''}
                                onChange={(e) => handleInstructionChange(index, 'title', e.target.value)}
                                placeholder="Instruction Title (e.g., 'Hot Tub Usage', 'Parking Info')"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium bg-white"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveInstruction(index)}
                                className="ml-3 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center space-x-1 text-sm font-medium"
                                title="Remove instruction"
                              >
                                <SafeIcon icon={FiTrash2} />
                                <span>Remove</span>
                              </button>
                            </div>
                            <div className="w-full">
                              <RichTextEditor
                                value={instruction.content || ''}
                                onChange={(value) => handleInstructionChange(index, 'content', value)}
                                placeholder="Enter detailed instructions here..."
                                className="min-h-[120px]"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={handleAddInstruction}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        <SafeIcon icon={FiPlus} />
                        <span>Add Instruction</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Support Contact Tab */}
          {activeTab === 'support' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Support Contact</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Provide contact information for guest support (phone, email, etc.)
                </p>
                <RichTextEditor
                  value={formData.supportContact}
                  onChange={(value) => handleRichTextChange('supportContact', value)}
                  placeholder="Phone: (555) 123-4567, Email: support@example.com"
                />
              </div>
            </div>
          )}

          {/* Local Recommendations Tab */}
          {activeTab === 'local' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Local Recommendations</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Share your favorite restaurants, grocery stores, attractions, and local tips
                </p>
                <RichTextEditor
                  value={formData.localRecommendations}
                  onChange={(value) => handleRichTextChange('localRecommendations', value)}
                  placeholder="Best restaurants, grocery stores, attractions..."
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer with Save/Cancel buttons */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-white transition-colors duration-200"
          >
            Cancel
          </button>
          <motion.button
            type="submit"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiSave} />
            <span>{property ? 'Update' : 'Create'} Property</span>
          </motion.button>
        </div>
      </form>
    </motion.div>
  );
};

export default PropertyForm;
